import { Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/loading";
import { EmptyState } from "@/components/ui/empty-state";
import { useTLGroupsWithStudents } from "@/hooks/use-tl-dashboard";
import { bidiText } from "@/lib/utils";
import {
  FolderOpen,
  ArrowRight,
  Users,
  BookOpen,
  GraduationCap,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";

export function GroupsSection() {
  const {
    data: groups = [],
    isLoading: groupsLoading,
    isError: groupsError,
    refetch: refetchGroups,
  } = useTLGroupsWithStudents();

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-primary" aria-hidden="true" />
          My Groups
        </h2>
        <Button asChild variant="outline" size="sm" className="gap-1">
          <Link to="/team-leader/groups">
            <span>View All</span>
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {groupsLoading ? (
        <CardSkeleton count={3} />
      ) : groupsError ? (
        <div className="p-8 text-center space-y-3 rounded-xl border bg-card">
          <div className="flex items-center justify-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <span className="text-sm font-medium">Could not load groups.</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetchGroups()}>
            Try again
          </Button>
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title="No groups assigned"
          description="Contact your administrator to be assigned to groups."
          className="py-12"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => {
            const studentCount = group.students?.length ?? group.studentCount ?? 0;
            const trackName = group.tracks && group.tracks.length > 0 ? group.tracks[0].name : null;

            return (
              <Link
                key={group.id}
                to="/team-leader/groups/$groupId"
                params={{ groupId: group.id }}
                className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group"
              >
                <Card className="h-full transition-all duration-200 hover:shadow-md border-l-4 border-l-primary/40 hover:border-l-primary cursor-pointer">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3
                          className="font-semibold text-base text-foreground truncate"
                          {...bidiText}
                        >
                          {group.name}
                        </h3>
                        <ChevronRight
                          className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1"
                          aria-hidden="true"
                        />
                      </div>

                      {group.description && (
                        <p
                          className="text-xs text-muted-foreground line-clamp-1 mb-3"
                          {...bidiText}
                        >
                          {group.description}
                        </p>
                      )}

                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="h-3.5 w-3.5" aria-hidden="true" />
                          <span>
                            {studentCount} student{studentCount !== 1 ? "s" : ""}
                          </span>
                        </div>

                        {trackName && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                            <span className="truncate" {...bidiText}>
                              {trackName}
                            </span>
                          </div>
                        )}

                        {group.courses && group.courses.length > 0 && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <GraduationCap className="h-3.5 w-3.5" aria-hidden="true" />
                            <span>
                              {group.courses.length} course
                              {group.courses.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {(group.tracks?.length > 0 || group.courses?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-4 pt-2 border-t">
                        {group.tracks?.slice(0, 2).map((t) => (
                          <Badge
                            key={t.id}
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0"
                            {...bidiText}
                          >
                            {t.name}
                          </Badge>
                        ))}
                        {group.courses?.slice(0, 2).map((c) => (
                          <Badge
                            key={c.id}
                            variant="outline"
                            className="text-[10px] px-1.5 py-0"
                            {...bidiText}
                          >
                            {c.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
